import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { AlertTriangle, CheckCircle, Info, FileText } from 'lucide-react';

function D1_Overview() {
  const { dataE, updateDataRaw, nextStep } = useWizard();
  const overview = dataE?.overview || {
    involvesHumanParticipants: null,
    involvesHumanTissue: null,
    involvesPersonalData: null
  };

  const updateOverview = (field, value) => {
    updateDataRaw({
      'wizardE.overview': { ...overview, [field]: value }
    });
  };

  const allAnswered = overview.involvesHumanParticipants !== null &&
                      overview.involvesHumanTissue !== null &&
                      overview.involvesPersonalData !== null;

  const needsEthicsApproval = overview.involvesHumanParticipants === true ||
                              overview.involvesHumanTissue === true ||
                              overview.involvesPersonalData === true;

  const canProceed = allAnswered && needsEthicsApproval;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <FileText className="step-icon" size={32} />
        <div>
          <h2>Step 1: Ethics Overview</h2>
          <p className="step-description">
            Determine if your research requires ethics approval by answering the following questions.
          </p>
        </div>
      </div>

      <div className="info-box info">
        <Info size={20} />
        <div>
          <strong>About Ethics Approval</strong>
          <p>
            Research involving human participants, their data, or biological samples typically requires 
            ethics committee approval before data collection can begin. This wizard will help you 
            prepare your ethics application, including for independent researchers who lack access to 
            an institutional review board but must still demonstrate compliance with international 
            best-practice ethical standards.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Eligibility Screening</h3>
        <p className="section-description">
          Please answer the following questions to determine if your research requires ethics approval.
        </p>

        <div className="eligibility-question">
          <div className="question-label">
            Does your research involve human participants?
          </div>
          <p className="question-help">
            This includes interviews, surveys, observations, experiments, or any direct interaction with people for research purposes.
          </p>
          <div className="radio-group horizontal">
            <label className="radio-option">
              <input
                type="radio"
                name="involvesHumanParticipants"
                checked={overview.involvesHumanParticipants === true}
                onChange={() => updateOverview('involvesHumanParticipants', true)}
              />
              <span className="radio-label">Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="involvesHumanParticipants"
                checked={overview.involvesHumanParticipants === false}
                onChange={() => updateOverview('involvesHumanParticipants', false)}
              />
              <span className="radio-label">No</span>
            </label>
          </div>
        </div>

        <div className="eligibility-question">
          <div className="question-label">
            Does your research involve human tissue, cells, or biological samples?
          </div>
          <p className="question-help">
            This includes blood, saliva, tissue samples, cell lines, or any biological material derived from humans.
          </p>
          <div className="radio-group horizontal">
            <label className="radio-option">
              <input
                type="radio"
                name="involvesHumanTissue"
                checked={overview.involvesHumanTissue === true}
                onChange={() => updateOverview('involvesHumanTissue', true)}
              />
              <span className="radio-label">Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="involvesHumanTissue"
                checked={overview.involvesHumanTissue === false}
                onChange={() => updateOverview('involvesHumanTissue', false)}
              />
              <span className="radio-label">No</span>
            </label>
          </div>
        </div>

        <div className="eligibility-question">
          <div className="question-label">
            Does your research involve collecting or processing personal data?
          </div>
          <p className="question-help">
            This includes any information relating to an identifiable person, such as names, contact details, 
            opinions, health records, or any data that could identify someone directly or indirectly.
          </p>
          <div className="radio-group horizontal">
            <label className="radio-option">
              <input
                type="radio"
                name="involvesPersonalData"
                checked={overview.involvesPersonalData === true}
                onChange={() => updateOverview('involvesPersonalData', true)}
              />
              <span className="radio-label">Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="involvesPersonalData"
                checked={overview.involvesPersonalData === false}
                onChange={() => updateOverview('involvesPersonalData', false)}
              />
              <span className="radio-label">No</span>
            </label>
          </div>
        </div>
      </div>

      {allAnswered && (
        <div className={`eligibility-result ${needsEthicsApproval ? 'requires-approval' : 'no-approval'}`}>
          {needsEthicsApproval ? (
            <>
              <CheckCircle size={24} className="result-icon success" />
              <div>
                <strong>Ethics approval is required</strong>
                <p>
                  Based on your responses, your research requires ethics committee approval. 
                  Continue through this wizard to prepare your application.
                </p>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle size={24} className="result-icon warning" />
              <div>
                <strong>Ethics approval may not be required</strong>
                <p>
                  Based on your responses, your research may not require formal ethics approval. 
                  However, we recommend consulting with your institution's ethics committee to confirm.
                  This wizard is designed for research involving human participants or their data.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      <NavigationButtons
        onNext={canProceed ? nextStep : null}
        disableNext={!canProceed}
        nextLabel="Continue to Research Team"
      />
    </div>
  );
}

export default D1_Overview;
